import { useTranslation } from 'next-i18next'
import React, { useState } from 'react'

import { SITE_URL } from '@goodfellas/environment'

import Button from './button'

type ShareAppProps = {
  /** Override the URL that gets shared. Defaults to the public site URL. */
  url?: string
  /** Override the invite message. Defaults to the i18n invite text. */
  message?: string
  /** Button color (see ./button color map). Defaults to the brand red. */
  color?: string
  className?: string
}

const DEFAULT_SITE_URL = 'https://goodfellas.app/'

/**
 * "Invite your friends to the app" button.
 *
 * Uses the native Web Share API when available (mobile / Capacitor), and
 * gracefully falls back to opening a pre-filled WhatsApp message (and copying
 * the link to the clipboard) on platforms without `navigator.share`.
 *
 * Shown while a fighter's card is not ready yet, turning the empty state into
 * a growth loop: invite training partners → they evaluate you → your card is
 * generated.
 */
export default function ShareApp({
  url,
  message,
  color = 'red',
  className,
}: ShareAppProps) {
  const { t } = useTranslation('avaliacao360')
  const [copied, setCopied] = useState(false)

  const shareUrl = url || SITE_URL || DEFAULT_SITE_URL
  const shareTitle = String(t('shareApp.title', 'Goodfellas'))
  const shareText =
    message ||
    String(
      t(
        'shareApp.text',
        'Bora treinar e se desafiar no jiu-jitsu? Te espero no Goodfellas!',
      ),
    )

  function notifyCopied() {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return
    navigator.clipboard
      .writeText(`${shareText} ${shareUrl}`)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2500)
      })
      .catch(() => undefined)
  }

  function fallbackShare() {
    const fullMessage = `${shareText} ${shareUrl}`
    if (typeof window !== 'undefined') {
      window.open(
        `https://wa.me/?text=${encodeURIComponent(fullMessage)}`,
        '_blank',
        'noopener,noreferrer',
      )
    }
    notifyCopied()
  }

  async function handleShare() {
    const shareData = { title: shareTitle, text: shareText, url: shareUrl }

    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.share === 'function'
    ) {
      try {
        await navigator.share(shareData)
        return
      } catch (err) {
        // User dismissed the native sheet: do nothing.
        if ((err as { name?: string })?.name === 'AbortError') return
        // Otherwise fall back to the WhatsApp/clipboard flow.
      }
    }

    fallbackShare()
  }

  return (
    <div className={`flex flex-col items-center gap-2 ${className ?? ''}`}>
      <Button
        label={String(t('shareApp.button', 'Convidar amigos pro app'))}
        color={color}
        action={handleShare}
      />
      {copied ? (
        <span className="text-white/70 text-xs">
          {t('shareApp.copied', 'Link copiado!')}
        </span>
      ) : null}
    </div>
  )
}
